import { MapPin, Clock, Phone, ExternalLink, Map as MapIcon, Instagram } from "lucide-react";
import { useState } from "react";
import { DEALER, SALES } from "../data/constants";
import { useSettings, buildWaLink } from "../lib/SettingsContext";
import { trackWhatsAppLead } from "../lib/AnalyticsInjector";

export default function DealerLocation() {
  const { settings } = useSettings();
  const [mapLoaded, setMapLoaded] = useState(false);
  const dealerName = settings.dealer_name || DEALER.name;
  const address = settings.dealer_address || DEALER.address;
  const salesName = settings.sales_name || SALES.name;
  const salesTitle = settings.sales_title || SALES.title;
  const phone = settings.sales_phone || SALES.phone;
  const phoneIntl = settings.sales_phone_intl || SALES.phoneIntl;
  const igUrl = settings.instagram_url || "https://instagram.com/bydcibubur.official";
  const igHandle = settings.instagram_handle || "@bydcibubur.official";
  const dealerImage = settings.dealer_image || DEALER.image;
  return (
    <section
      className="bg-white border-t border-[#E5E5EA]"
      data-testid="dealer-location-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.25em] text-[#6E6E73] mb-5">
              Dealer Resmi
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0F0F0F] leading-[1.1]">
              {dealerName}
            </h2>
            <p className="mt-6 text-base text-[#6E6E73] leading-relaxed">
              Showroom resmi BYD dengan fasilitas display 7 unit, test drive
              langsung, bengkel service terpadu, dan tim after-sales
              bersertifikat.
            </p>

            <div className="mt-8 space-y-5">
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 mt-0.5 text-[#0F0F0F] shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[#0F0F0F]">Alamat</p>
                  <p className="text-sm text-[#6E6E73] leading-relaxed mt-1">
                    {DEALER.address}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock className="w-5 h-5 mt-0.5 text-[#0F0F0F] shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[#0F0F0F]">
                    Jam Operasional
                  </p>
                  <p className="text-sm text-[#6E6E73] leading-relaxed mt-1">
                    Senin - Sabtu, 08:30 - 17:30 WIB
                    <br />
                    Minggu tutup (appointment via WhatsApp)
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="w-5 h-5 mt-0.5 text-[#0F0F0F] shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[#0F0F0F]">
                    Kontak Langsung
                  </p>
                  <p className="text-sm text-[#6E6E73] leading-relaxed mt-1">
                    {salesName} — {salesTitle}
                    <br />
                    <a
                      href={`tel:+${phoneIntl}`}
                      className="text-[#0F0F0F] underline underline-offset-4 hover:no-underline"
                    >
                      +62 {phone.slice(1)}
                    </a>
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Instagram className="w-5 h-5 mt-0.5 text-[#0F0F0F] shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[#0F0F0F]">Instagram</p>
                  <a
                    href={igUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#0F0F0F] underline underline-offset-4 hover:no-underline mt-1 inline-block"
                    data-testid="dealer-instagram"
                  >
                    {igHandle}
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href={DEALER.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0F0F0F] text-white px-6 py-3.5 text-sm font-medium hover:bg-[#333333] transition-colors"
                data-testid="dealer-directions-cta"
              >
                Buka di Google Maps
                <ExternalLink className="w-4 h-4" />
              </a>
              <a
                href={buildWaLink(
                  settings,
                  `Halo ${salesName}, saya ingin buat janji kunjungan ke showroom ${dealerName}.`
                )}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppLead("dealer-appointment")}
                className="inline-flex items-center justify-center rounded-full bg-white text-[#0F0F0F] px-6 py-3.5 text-sm font-medium border border-[#E5E5EA] hover:bg-[#F5F5F7] transition-colors"
                data-testid="dealer-appointment-cta"
              >
                Buat Janji Kunjungan
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="aspect-[4/3] lg:aspect-[5/4] rounded-3xl overflow-hidden border border-[#E5E5EA] relative">
              <img
                src={dealerImage}
                alt={`Showroom ${dealerName}`}
                loading="lazy"
                decoding="async"
                width="1200"
                height="900"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="mt-4 aspect-[16/6] rounded-2xl overflow-hidden border border-[#E5E5EA] relative bg-[#F5F5F7]">
              {mapLoaded ? (
                <iframe
                  title="Peta BYD Haka Auto Cibubur"
                  src={DEALER.mapEmbed}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              ) : (
                <button
                  onClick={() => setMapLoaded(true)}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-3 hover:bg-[#EFEFF0] transition-colors"
                  data-testid="load-map-button"
                  aria-label="Muat peta Google Maps"
                >
                  <MapIcon className="w-8 h-8 text-[#6E6E73]" strokeWidth={1.5} />
                  <span className="text-sm text-[#6E6E73]">Klik untuk muat peta</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
